import React from 'react';
import { string, oneOfType, bool } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import {
  ensureUser,
  ensureCurrentUser,
  userDisplayNameAsString,
  userAbbreviatedName,
} from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';

import { ResponsiveImage, IconBannedUser, NamedLink } from '../../components/';

import css from './Avatar.module.css';

// Responsive image sizes hint
const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

const AVATAR_IMAGE_VARIANTS = [
  // 40x40
  'square-xsmall',

  // 80x80
  'square-xsmall2x',

  // 240x240
  'square-small',

  // 480x480
  'square-small2x',
];

export const AvatarComponent = props => {
  const {
    rootClassName,
    className,
    initialsClassName,
    user,
    renderSizes,
    disableProfileLink,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);
  // I.e. the status is active, not pending-approval or banned
  const isUnauthorizedUser = userIsCurrentUser && !isUserAuthorized(user);
  const variant = user?.attributes?.state;
  //'pending-approval'

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const defaultUserDisplayName = isBannedUser
    ? intl.formatMessage({ id: 'Avatar.bannedUserDisplayName' })
    : isDeletedUser
    ? intl.formatMessage({ id: 'Avatar.deletedUserDisplayName' })
    : '';

  const defaultUserAbbreviatedName = '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
  const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
  const rootProps = { className: classes, title: displayName };
  const linkProps =
    isUnauthorizedUser && avatarUser.id
      ? {
          name: 'ProfilePageVariant',
          params: { id: avatarUser.id.uuid, variant },
        }
      : avatarUser.id
      ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid } }
      : { name: 'ProfileBasePage' };
  const hasProfileImage = avatarUser.profileImage && avatarUser.profileImage.id;
  const profileLinkEnabled = !disableProfileLink;

  if (isBannedUser || isDeletedUser) {
    return (
      <div {...rootProps}>
        <IconBannedUser className={css.bannedUserIcon} />
      </div>
    );
  } else if (hasProfileImage && profileLinkEnabled) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarUser.profileImage}
          variants={AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
      </NamedLink>
    );
  } else if (hasProfileImage) {
    return (
      <div {...rootProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarUser.profileImage}
          variants={AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
      </div>
    );
  } else if (profileLinkEnabled) {
    // Placeholder avatar (initials)
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <div className={css.profileIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
            <g id="Frame" clip-path="url(#clip0_2558_120)">
              <g id="Group">
                <path id="Vector" d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z" fill="#58D1D6" />
                <path id="Vector_2" d="M29.4975 11.1181C29.4975 16.2463 25.3334 20.4104 20.2051 20.4104C15.0769 20.4104 10.9128 16.2463 10.9128 11.1181C10.9128 5.98986 15.0769 1.82576 20.2051 1.82576C25.3334 1.80525 29.4975 5.96935 29.4975 11.1181Z" fill="#9045D6" />
                <g id="Group_2">
                  <path id="Vector_3" d="M10.7898 20.3692L10.8103 20.3897C12.0206 21.6 13.9693 21.6 15.159 20.3897L18.1129 17.4359C19.3231 16.2256 19.3231 14.2769 18.1129 13.0872L18.0924 13.0667L10.7898 20.3692Z" fill="#9045D6" />
                  <path id="Vector_4" d="M14.6257 24.2667C14.6462 24.2667 14.6462 24.2667 14.6668 24.2667C16.3693 24.2667 17.7437 22.8923 17.7437 21.1898V17.0257C17.7437 15.3231 16.3693 13.9487 14.6668 13.9487C14.6462 13.9487 14.6462 13.9487 14.6257 13.9487V24.2667Z" fill="#9045D6" />
                </g>
                <path id="Vector_5" d="M29.6819 20.3692L29.6614 20.3897C28.4512 21.6 26.5025 21.6 25.3127 20.3897L22.3589 17.4359C21.1486 16.2256 21.1486 14.2769 22.3589 13.0872L22.3794 13.0667L29.6819 20.3692Z" fill="#9045D6" />
                <path id="Vector_6" d="M30.8104 17.2923L30.7898 17.3128C29.5796 18.5231 27.6309 18.5231 26.4411 17.3128L23.4873 14.359C22.277 13.1487 22.277 11.2 23.4873 10.0103L23.5078 9.98975L30.8104 17.2923Z" fill="#9045D6" />
                <path id="Vector_7" d="M9.70264 17.2923L9.72315 17.3128C10.9334 18.5231 12.8821 18.5231 14.0719 17.3128L17.0462 14.359C18.2565 13.1487 18.2565 11.2 17.0462 10.0103L17.0257 9.98975L9.70264 17.2923Z" fill="#9045D6" />
                <path id="Vector_8" d="M25.8462 24.2667C25.8257 24.2667 25.8257 24.2667 25.8052 24.2667C24.1026 24.2667 22.7283 22.8923 22.7283 21.1898V17.0257C22.7283 15.3231 24.1026 13.9487 25.8052 13.9487C25.8257 13.9487 25.8257 13.9487 25.8462 13.9487V24.2667Z" fill="#9045D6" />
                <g id="Group_3">
                  <path id="Vector_9" d="M14.5026 14.5641C15.0564 15.9179 14.8103 17.3128 13.9282 17.6615C13.0462 18.0307 11.8769 17.2307 11.3231 15.8769C10.7692 14.523 11.0154 13.1282 11.8975 12.7794C12.759 12.4307 13.9282 13.2307 14.5026 14.5641Z" fill="#FFEFCF" />
                  <path id="Vector_10" d="M25.9898 14.5641C25.436 15.9179 25.6821 17.3128 26.5642 17.6615C27.4463 18.0307 28.6155 17.2307 29.1693 15.8769C29.7232 14.523 29.477 13.1282 28.595 12.7794C27.7334 12.4307 26.5642 13.2307 25.9898 14.5641Z" fill="#FFEFCF" />
                </g>
                <path id="Vector_11" d="M20 39.9999C26.0923 39.9999 31.5282 37.2717 35.2 32.9845C33.4359 30.0101 27.3641 28.2255 23.4666 26.5024H16.9641C12.3282 28.5947 6.46152 30.2153 5.08716 33.3332C8.75895 37.4153 14.0718 39.9999 20 39.9999Z" fill="#1A1887" />
                <path id="Vector_12" d="M17.0667 28.5538C17.0667 30.2974 18.482 31.7333 20.2461 31.7333C21.9897 31.7333 23.4256 30.318 23.4256 28.5538V21.1692C23.4256 19.4256 22.0102 17.9897 20.2461 17.9897C18.5025 17.9897 17.0667 19.4051 17.0667 21.1692V28.5538Z" fill="#FFEFCF" />
                <path id="Vector_13" opacity="0.8" d="M20.2461 18.9949C21.9897 18.9949 23.4256 20.4103 23.4256 22.1744V22.6256C22.0512 24.0821 20.2461 24.9641 18.2564 24.9641C17.8461 24.9641 17.4564 24.9231 17.0871 24.8615V22.1744C17.0666 20.4103 18.5025 18.9949 20.2461 18.9949Z" fill="#FFEFCF" />
                <path id="Vector_14" d="M27.9794 13.9897C27.9794 19.0154 24.5128 22.9539 20.2461 22.9539C15.9794 22.9539 12.5128 19.0154 12.5128 13.9897C12.5128 8.96411 11.8358 2.97437 20.2461 2.97437C28.6564 2.97437 27.9794 8.94359 27.9794 13.9897Z" fill="#FFEFCF" />
                <path id="Vector_15" d="M17.4768 27.6309L20.3691 31.6924L23.1589 27.6309L20.5742 39.3847L17.4768 27.6309Z" fill="#1A1887" />
                <path id="Vector_16" d="M12.718 28.3693L15.3026 24.8616H17.477L20.4924 39.1385L12.718 28.3693Z" fill="#1A1887" />
                <path id="Vector_17" d="M27.795 28.2667L25.1283 24.8616H23.0565L20.5745 39.3847L27.795 28.2667Z" fill="#1A1887" />
                <path id="Vector_18" d="M25.8872 7.03589C25.8872 7.03589 27.6718 10.5641 27.8975 12.1231C28.1641 9.49743 25.8872 7.03589 25.8872 7.03589Z" fill="#FFEFCF" />
                <path id="Vector_19" d="M18.3179 18.4819C18.3179 18.4819 19.1384 19.4255 20.2871 19.4255C21.4358 19.4255 22.2563 18.4819 22.2563 18.4819H18.3179Z" fill="#FFEFCF" />
                <path id="Vector_20" d="M11.7539 13.7436C11.7744 13.7436 11.7744 13.7436 11.7949 13.7436C13.4975 13.7436 14.8719 12.3692 14.8719 10.6667V4.45127C14.8719 3.6923 13.1078 5.41538 11.7949 7.67179C11.7949 7.67179 11.7744 7.71281 11.7539 7.71281V13.7436Z" fill="#9045D6" />
                <path id="Vector_21" d="M13.3948 5.94871V9.80512C13.4153 9.80512 13.4153 9.80512 13.4358 9.80512C15.2614 9.80512 16.7589 8.3282 16.7589 6.48204V3.03589C15.0563 3.65127 14.0307 4.67691 13.3948 5.94871Z" fill="#9045D6" />
                <path id="Vector_22" d="M28.4102 13.7437C28.3897 13.7437 28.3897 13.7437 28.3692 13.7437C26.6666 13.7437 25.2922 12.3693 25.2922 10.6667V4.06162C25.2922 3.30264 27.0563 5.39495 28.3692 7.65136C28.3692 7.65136 28.3897 7.69239 28.4102 7.69239V13.7437Z" fill="#9045D6" />
                <path id="Vector_23" d="M24.4308 4.96414C22.5642 4.96414 20.9847 3.63081 20.6359 1.84619H19.6513C17.518 1.84619 15.7949 3.56927 15.7949 5.7026C15.7949 7.83593 17.518 9.55901 19.6513 9.55901H26.2565C28.3898 9.55901 30.1129 7.83593 30.1129 5.7026C30.1129 5.43593 30.0924 5.18978 30.0308 4.94363H24.4308V4.96414Z" fill="#9045D6" />
                <path id="Vector_24" d="M25.9691 4.73857C25.7845 5.55908 24.4102 5.96934 22.8512 5.64113C21.3127 5.31293 20.205 4.38985 20.3691 3.54882C20.5538 2.72831 21.9281 2.31805 23.4871 2.64626C25.0256 2.97446 26.1332 3.91805 25.9691 4.73857Z" fill="#9045D6" />
              </g>
            </g>
            <defs>
              <clipPath id="clip0_2558_120">
                <rect width="40" height="40" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
        {/* <span className={initialsClassName || css.initials}>{abbreviatedName}</span> */}
      </NamedLink>
    );
  } else {
    // Placeholder avatar (initials)
    return (
      <div {...rootProps}>
         <div className={css.profileIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
            <g id="Frame" clip-path="url(#clip0_2558_120)">
              <g id="Group">
                <path id="Vector" d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z" fill="#58D1D6" />
                <path id="Vector_2" d="M29.4975 11.1181C29.4975 16.2463 25.3334 20.4104 20.2051 20.4104C15.0769 20.4104 10.9128 16.2463 10.9128 11.1181C10.9128 5.98986 15.0769 1.82576 20.2051 1.82576C25.3334 1.80525 29.4975 5.96935 29.4975 11.1181Z" fill="#9045D6" />
                <g id="Group_2">
                  <path id="Vector_3" d="M10.7898 20.3692L10.8103 20.3897C12.0206 21.6 13.9693 21.6 15.159 20.3897L18.1129 17.4359C19.3231 16.2256 19.3231 14.2769 18.1129 13.0872L18.0924 13.0667L10.7898 20.3692Z" fill="#9045D6" />
                  <path id="Vector_4" d="M14.6257 24.2667C14.6462 24.2667 14.6462 24.2667 14.6668 24.2667C16.3693 24.2667 17.7437 22.8923 17.7437 21.1898V17.0257C17.7437 15.3231 16.3693 13.9487 14.6668 13.9487C14.6462 13.9487 14.6462 13.9487 14.6257 13.9487V24.2667Z" fill="#9045D6" />
                </g>
                <path id="Vector_5" d="M29.6819 20.3692L29.6614 20.3897C28.4512 21.6 26.5025 21.6 25.3127 20.3897L22.3589 17.4359C21.1486 16.2256 21.1486 14.2769 22.3589 13.0872L22.3794 13.0667L29.6819 20.3692Z" fill="#9045D6" />
                <path id="Vector_6" d="M30.8104 17.2923L30.7898 17.3128C29.5796 18.5231 27.6309 18.5231 26.4411 17.3128L23.4873 14.359C22.277 13.1487 22.277 11.2 23.4873 10.0103L23.5078 9.98975L30.8104 17.2923Z" fill="#9045D6" />
                <path id="Vector_7" d="M9.70264 17.2923L9.72315 17.3128C10.9334 18.5231 12.8821 18.5231 14.0719 17.3128L17.0462 14.359C18.2565 13.1487 18.2565 11.2 17.0462 10.0103L17.0257 9.98975L9.70264 17.2923Z" fill="#9045D6" />
                <path id="Vector_8" d="M25.8462 24.2667C25.8257 24.2667 25.8257 24.2667 25.8052 24.2667C24.1026 24.2667 22.7283 22.8923 22.7283 21.1898V17.0257C22.7283 15.3231 24.1026 13.9487 25.8052 13.9487C25.8257 13.9487 25.8257 13.9487 25.8462 13.9487V24.2667Z" fill="#9045D6" />
                <g id="Group_3">
                  <path id="Vector_9" d="M14.5026 14.5641C15.0564 15.9179 14.8103 17.3128 13.9282 17.6615C13.0462 18.0307 11.8769 17.2307 11.3231 15.8769C10.7692 14.523 11.0154 13.1282 11.8975 12.7794C12.759 12.4307 13.9282 13.2307 14.5026 14.5641Z" fill="#FFEFCF" />
                  <path id="Vector_10" d="M25.9898 14.5641C25.436 15.9179 25.6821 17.3128 26.5642 17.6615C27.4463 18.0307 28.6155 17.2307 29.1693 15.8769C29.7232 14.523 29.477 13.1282 28.595 12.7794C27.7334 12.4307 26.5642 13.2307 25.9898 14.5641Z" fill="#FFEFCF" />
                </g>
                <path id="Vector_11" d="M20 39.9999C26.0923 39.9999 31.5282 37.2717 35.2 32.9845C33.4359 30.0101 27.3641 28.2255 23.4666 26.5024H16.9641C12.3282 28.5947 6.46152 30.2153 5.08716 33.3332C8.75895 37.4153 14.0718 39.9999 20 39.9999Z" fill="#1A1887" />
                <path id="Vector_12" d="M17.0667 28.5538C17.0667 30.2974 18.482 31.7333 20.2461 31.7333C21.9897 31.7333 23.4256 30.318 23.4256 28.5538V21.1692C23.4256 19.4256 22.0102 17.9897 20.2461 17.9897C18.5025 17.9897 17.0667 19.4051 17.0667 21.1692V28.5538Z" fill="#FFEFCF" />
                <path id="Vector_13" opacity="0.8" d="M20.2461 18.9949C21.9897 18.9949 23.4256 20.4103 23.4256 22.1744V22.6256C22.0512 24.0821 20.2461 24.9641 18.2564 24.9641C17.8461 24.9641 17.4564 24.9231 17.0871 24.8615V22.1744C17.0666 20.4103 18.5025 18.9949 20.2461 18.9949Z" fill="#FFEFCF" />
                <path id="Vector_14" d="M27.9794 13.9897C27.9794 19.0154 24.5128 22.9539 20.2461 22.9539C15.9794 22.9539 12.5128 19.0154 12.5128 13.9897C12.5128 8.96411 11.8358 2.97437 20.2461 2.97437C28.6564 2.97437 27.9794 8.94359 27.9794 13.9897Z" fill="#FFEFCF" />
                <path id="Vector_15" d="M17.4768 27.6309L20.3691 31.6924L23.1589 27.6309L20.5742 39.3847L17.4768 27.6309Z" fill="#1A1887" />
                <path id="Vector_16" d="M12.718 28.3693L15.3026 24.8616H17.477L20.4924 39.1385L12.718 28.3693Z" fill="#1A1887" />
                <path id="Vector_17" d="M27.795 28.2667L25.1283 24.8616H23.0565L20.5745 39.3847L27.795 28.2667Z" fill="#1A1887" />
                <path id="Vector_18" d="M25.8872 7.03589C25.8872 7.03589 27.6718 10.5641 27.8975 12.1231C28.1641 9.49743 25.8872 7.03589 25.8872 7.03589Z" fill="#FFEFCF" />
                <path id="Vector_19" d="M18.3179 18.4819C18.3179 18.4819 19.1384 19.4255 20.2871 19.4255C21.4358 19.4255 22.2563 18.4819 22.2563 18.4819H18.3179Z" fill="#FFEFCF" />
                <path id="Vector_20" d="M11.7539 13.7436C11.7744 13.7436 11.7744 13.7436 11.7949 13.7436C13.4975 13.7436 14.8719 12.3692 14.8719 10.6667V4.45127C14.8719 3.6923 13.1078 5.41538 11.7949 7.67179C11.7949 7.67179 11.7744 7.71281 11.7539 7.71281V13.7436Z" fill="#9045D6" />
                <path id="Vector_21" d="M13.3948 5.94871V9.80512C13.4153 9.80512 13.4153 9.80512 13.4358 9.80512C15.2614 9.80512 16.7589 8.3282 16.7589 6.48204V3.03589C15.0563 3.65127 14.0307 4.67691 13.3948 5.94871Z" fill="#9045D6" />
                <path id="Vector_22" d="M28.4102 13.7437C28.3897 13.7437 28.3897 13.7437 28.3692 13.7437C26.6666 13.7437 25.2922 12.3693 25.2922 10.6667V4.06162C25.2922 3.30264 27.0563 5.39495 28.3692 7.65136C28.3692 7.65136 28.3897 7.69239 28.4102 7.69239V13.7437Z" fill="#9045D6" />
                <path id="Vector_23" d="M24.4308 4.96414C22.5642 4.96414 20.9847 3.63081 20.6359 1.84619H19.6513C17.518 1.84619 15.7949 3.56927 15.7949 5.7026C15.7949 7.83593 17.518 9.55901 19.6513 9.55901H26.2565C28.3898 9.55901 30.1129 7.83593 30.1129 5.7026C30.1129 5.43593 30.0924 5.18978 30.0308 4.94363H24.4308V4.96414Z" fill="#9045D6" />
                <path id="Vector_24" d="M25.9691 4.73857C25.7845 5.55908 24.4102 5.96934 22.8512 5.64113C21.3127 5.31293 20.205 4.38985 20.3691 3.54882C20.5538 2.72831 21.9281 2.31805 23.4871 2.64626C25.0256 2.97446 26.1332 3.91805 25.9691 4.73857Z" fill="#9045D6" />
              </g>
            </g>
            <defs>
              <clipPath id="clip0_2558_120">
                <rect width="40" height="40" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
        {/* <span className={initialsClassName || css.initials}>{abbreviatedName}</span> */}
      </div>
    );
  }
};

AvatarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  user: null,
  renderSizes: AVATAR_SIZES,
  disableProfileLink: false,
};

AvatarComponent.propTypes = {
  rootClassName: string,
  className: string,
  user: oneOfType([propTypes.user, propTypes.currentUser]),

  renderSizes: string,
  disableProfileLink: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const Avatar = injectIntl(AvatarComponent);

export default Avatar;

export const AvatarSmall = props => (
  <Avatar
    rootClassName={css.smallAvatar}
    initialsClassName={css.initialsSmall}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarSmall.displayName = 'AvatarSmall';

export const AvatarMedium = props => (
  <Avatar
    rootClassName={css.mediumAvatar}
    initialsClassName={css.initialsMedium}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = props => (
  <Avatar
    rootClassName={css.largeAvatar}
    initialsClassName={css.initialsLarge}
    renderSizes={AVATAR_SIZES_LARGE}
    {...props}
  />
);
AvatarLarge.displayName = 'AvatarLarge';
